import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  tone?: 'accent' | 'positive' | 'caution' | 'critical';
  /** Legacy names. */
  variant?: 'default' | 'success' | 'error';
  showLabel?: boolean;
  label?: string;
}

const TONE = {
  accent: 'bg-accent',
  positive: 'bg-positive',
  caution: 'bg-caution',
  critical: 'bg-critical',
} as const;

const LEGACY = { default: 'accent', success: 'positive', error: 'critical' } as const;

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, max = 100, tone, variant, showLabel = false, label, ...props }, ref) => {
    const pct = Math.min(Math.max((value / max) * 100, 0), 100);
    const resolved = tone ?? (variant ? LEGACY[variant] : 'accent');

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={Math.round(value)}
          aria-label={label}
          className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2"
        >
          <div className={cn('h-full rounded-full transition-[width] duration-300 ease-out', TONE[resolved])} style={{ width: `${pct}%` }} />
        </div>
        {showLabel ? <span className="mt-1 block text-xs tabular-nums text-ink-2">{Math.round(pct)}%</span> : null}
      </div>
    );
  }
);

Progress.displayName = 'Progress';
